import { useTranslation } from "react-i18next";
import { useOrganizationSettings } from "./use-organization-settings";
import { useSelfCancellationAllowed } from "./use-self-cancellation";

/**
 * Hook for managing APPLICATION REVOKING (not shift cancellation).
 *
 * This handles when employees want to revoke their application BEFORE being assigned to a shift.
 * Uses `organizationSettings.revokePeriod` (number of days) to determine if revoking is allowed.
 *
 * Key differences from shift cancellation:
 *
 * - Applications: Employee has applied but NOT yet assigned → instant revocation, no approval
 * - Shifts: Employee IS assigned → requires manager approval (separate feature)
 *
 * Logic:
 *
 * - If revokePeriod === 0: always allow revoking (can revoke at any time)
 * - If revokePeriod > 0: only allow if shift starts in >= revokePeriod days
 *
 * Note: Shift cancellation uses `shiftCancellationPolicy` feature flag and will be implemented
 * separately with manager approval flow.
 */
export function useRevokePeriod() {
  const { t } = useTranslation();
  const { data: organization } = useOrganizationSettings();
  // Het afmeldbeleid van de organisatie staat bóven de dagengrens hieronder. Staat het op
  // `NOT_ALLOWED`, dan weigert de api het intrekken ook, en een knop die op een 400 eindigt is
  // erger dan geen knop. Dat de twee verschillende instellingen lezen is bestaande rommel
  // (`revokePeriod` hier, `shiftCancellationPolicy` daar) en geen onderdeel van deze wijziging.
  const selfCancellationAllowed = useSelfCancellationAllowed();

  // Get revoke period from organization settings (defaults to 0 = always allow)
  const revokePeriod = organization?.revokePeriod ?? 0;

  /**
   * Determines if an application can be revoked based on the organization's revoke period.
   *
   * Logic:
   *
   * - If revokePeriod === 0: always allow (employees can revoke at any time)
   * - If revokePeriod > 0: only allow if daysUntilStart >= revokePeriod
   *
   * @param startAt - The start date/time of the shift
   * @returns True if the application can be revoked, false otherwise
   */
  const canRevokeApplication = (startAt: Date | null | undefined | string) => {
    if (!selfCancellationAllowed) {
      return false;
    }

    if (!startAt) {
      return false;
    }

    // If revokePeriod is 0, always allow revoking
    if (revokePeriod === 0) {
      return true;
    }

    const startDate = typeof startAt === "string" ? new Date(startAt) : startAt;
    const now = new Date();

    // Calculate days using UTC to avoid timezone issues
    const startDateUTC = Date.UTC(
      startDate.getUTCFullYear(),
      startDate.getUTCMonth(),
      startDate.getUTCDate(),
    );
    const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const daysUntilStart = Math.floor((startDateUTC - nowUTC) / (1000 * 60 * 60 * 24));

    return daysUntilStart >= revokePeriod;
  };

  /**
   * Get human-readable text for the revoke period deadline.
   *
   * Examples:
   *
   * - RevokePeriod = 0: "op elk moment" (always)
   * - RevokePeriod = 1: "24 uur" (1 day = 24 hours)
   * - RevokePeriod = 2: "48 uur" (2 days = 48 hours)
   * - RevokePeriod = 3: "72 uur" (3 days = 72 hours)
   * - RevokePeriod = 7: "7 dagen" (7+ days shown as days)
   *
   * @returns Human-readable revoke period text
   */
  const getRevokePeriodText = () => {
    if (revokePeriod === 0) {
      return t("common:revoke-period.always-allowed");
    }

    // Show as hours for 1-3 days (more intuitive for short periods)
    if (revokePeriod <= 3) {
      const hours = revokePeriod * 24;
      return t("common:revoke-period.hours-before", { hours });
    }

    // Show as days for 4+ days
    return t("common:revoke-period.days-before", { days: revokePeriod });
  };

  /**
   * De zin die op de plek van de intrekknop komt, of `null` als er gewoon een knop staat.
   *
   * Een knop die zomaar weg is leest als een app die iets kwijt is; de medewerker gaat zoeken en
   * vindt niets. Deze regel zegt in één zin dat het niet aan hem ligt en wie het wél kan. Hij hoort
   * alleen bij `NOT_ALLOWED`: een verstreken dagengrens laat de knop óók verdwijnen, maar dat staat
   * al uitgelegd in de deadline-tekst en `getRevokePeriodText()` noemt de datum.
   */
  const revokeNotice = selfCancellationAllowed ? null : t("common:applications.revoke-not-allowed");

  return {
    revokePeriod,
    canRevokeApplication,
    getRevokePeriodText,
    selfCancellationAllowed,
    revokeNotice,
  };
}
